import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  fetchAdminInquiryAnswerRegist,
  fetchAdminInquiryDetail,
  fetchAdminInquiryUpdate,
} from '../../../api/admin.inquiry';
import { formatDate } from '../../../utils/util';
import { useForm } from 'react-hook-form';
import { TextArea } from '../../../components/items/joinInput';
import { AlertModal } from '../../../components/modal/alert.modal';
import { ConfirmModal } from '../../../components/modal/confirm.modal';

interface CreateInquiryAnswerDtoRx {
  inquiryId: number; // 1:1 문의 아이디
  createdAt: string; // 등록일 (ISO 8601 date-time 형식)
  passid: string; // 등록 아이디
  title: string; // 문의 제목
  question: string; // 문의 내용
  answer: string;
}

interface CreateInquiryAnswerDtoTx {
  inquiryId: number; // 1:1 문의 아이디
  answer: string; // 답변 내용
}

const AdminInquiryDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [inquiryDetailData, setInquiryDetailData] =
    useState<CreateInquiryAnswerDtoRx | null>(null);
  const [formData, setFormData] = useState<CreateInquiryAnswerDtoTx | null>(
    null,
  );
  const { register, handleSubmit, setValue } = useForm<{ answer: string }>();

  console.log(inquiryDetailData);

  // 모달
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [confirmModal, setConfirmModal] = useState(false);
  const [navigateOnClose, setNavigateOnClose] = useState(false); // 페이지 이동 여부를 추적

  const handleModalClose = () => {
    setIsModalOpen(false);
    if (navigateOnClose) {
      history.push({
        pathname: '/admin/inquiry/list',
      });
    }
  };

  const handleConfirmClose = () => {
    setConfirmModal(false);
  };

  useEffect(() => {
    const getInquiryDetailInfo = async () => {
      try {
        const response = await fetchAdminInquiryDetail(Number(id));
        setInquiryDetailData(response);
      } catch (error) {
        console.error(error);
      }
    };

    getInquiryDetailInfo();
  }, [id]);

  useEffect(() => {
    if (inquiryDetailData && inquiryDetailData.answer !== '') {
      setValue('answer', inquiryDetailData.answer);
    }
  }, [inquiryDetailData, setValue]);

  // 답변 등록 핸들러
  const handleAnswerRegist = async (data: CreateInquiryAnswerDtoTx) => {
    try {
      const response = await fetchAdminInquiryAnswerRegist(data);
      if (response.statusCode === 200) {
        setModalMessage('답변이 성공적으로 등록되었습니다.');
        setIsModalOpen(true);
        setNavigateOnClose(true);
      } else {
        setModalMessage('답변 등록에 실패했습니다.');
        setIsModalOpen(true);
        setNavigateOnClose(false);
      }
    } catch (error) {
      console.error(error);
      setModalMessage('답변 등록 중 오류가 발생했습니다.');
      setIsModalOpen(true);
    }
  };

  const handleAnswerUpdate = async (data: CreateInquiryAnswerDtoTx) => {
    if (formData?.answer === '') {
      setModalMessage('수정 시 답변이 빈칸이면 수정되지 않습니다.');
      setIsModalOpen(true);
      return;
    }

    try {
      const response = await fetchAdminInquiryUpdate(Number(id), data);
      if (response.statusCode === 200) {
        setModalMessage('답변이 성공적으로 수정되었습니다.');
        setIsModalOpen(true);
        setNavigateOnClose(true);
      } else {
        setModalMessage('답변 수정에 실패했습니다.');
        setIsModalOpen(true);
        setNavigateOnClose(false);
      }
    } catch (error) {
      console.error(error);
      setModalMessage('답변 수정 중 오류가 발생했습니다.');
      setIsModalOpen(true);
    }
  };

  // 폼 제출 시 실행될 함수
  const onSubmit = (data: { answer: string }) => {
    // 답변 데이터를 formData 상태에 저장하고 컨펌 모달을 띄움
    if (inquiryDetailData) {
      const submitData: CreateInquiryAnswerDtoTx = {
        inquiryId: inquiryDetailData.inquiryId,
        answer: data.answer,
      };
      setFormData(submitData);
      setConfirmModal(true);
    }
  };

  const cancleHandle = () => {
    history.push('/admin/inquiry/list');
  };

  const handleConfirmAction = () => {
    setConfirmModal(false);

    if (formData) {
      if (!inquiryDetailData?.answer) {
        // answer가 비어있거나 undefined/null인 경우 답변 등록
        handleAnswerRegist(formData);
      } else {
        // answer가 이미 존재하는 경우 답변 업데이트
        handleAnswerUpdate(formData);
      }
    }
  };

  return (
    <Container>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="inner">
          <div className="title">문의 답변 등록</div>
          <div className="detail-info-wrap">
            <ul>
              {inquiryDetailData ? (
                <>
                  <li className="rows-two">
                    <div className="l">
                      <span>등록 아이디</span>
                      <p>{inquiryDetailData.passid}</p>
                    </div>
                    <div className="r">
                      <span>등록일</span>
                      <p>{formatDate(inquiryDetailData.createdAt)}</p>
                    </div>
                  </li>
                  <li>
                    <span>제목</span>
                    <p>{inquiryDetailData.title}</p>
                  </li>
                  <li className="question">
                    <span>내용</span>
                    <div className="box">
                      <p>{inquiryDetailData.question}</p>
                    </div>
                  </li>
                  <li className="answer">
                    <span>답변</span>
                    <div className="box">
                      <TextArea
                        name="answer"
                        register={register}
                        placeholder="답변을 입력해주세요."
                      />
                    </div>
                  </li>
                </>
              ) : (
                <li>
                  <p className="no-info">회원 정보가 없습니다.</p>
                </li>
              )}
            </ul>
          </div>
          <div className="btn-box">
            <button className="cancle" onClick={cancleHandle}>
              취소
            </button>
            <button className="regist" type="submit">
              {inquiryDetailData?.answer === '' ? '등록' : '수정'}
            </button>
          </div>
        </div>
      </form>

      {confirmModal && (
        <ConfirmModal
          handleConfirmClose={handleConfirmClose}
          confirmText={
            inquiryDetailData?.answer === ''
              ? '등록하시겠습니까?'
              : '수정하시겠습니까?'
          }
          onClick={handleConfirmAction} // 확인 시 handleConfirmAction 호출
        />
      )}

      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose}
        />
      )}
    </Container>
  );
};

export default AdminInquiryDetail;

// style
const Container = styled.div`
  margin-top: 7rem;
  overflow-y: scroll;
  .inner {
    max-width: 70rem;
    width: 100%;
    margin: 0 auto;
    .title {
      font-size: 2rem;
      font-weight: 700;
      margin-bottom: 2.4rem;
    }
    .detail-info-wrap {
      ul {
        border-top: 2px solid #000;
        border-bottom: 2px solid #000;
        li {
          display: flex;
          align-items: center;
          padding: 0 0.8rem;
          height: 4.2rem;
          span {
            display: inline-flex;
            width: 9.6rem;
            font-size: 1.4rem;
            font-weight: 600;
            color: #222;
          }
          p {
            display: inline-flex;
            font-size: 1.4rem;
            color: #555;
            &.no-info {
              color: #000;
            }
          }
          &:not(:last-child) {
            border-bottom: 1px solid #eee;
          }
          &.rows-two {
            display: flex;
            justify-content: space-between;
            .r {
              p {
                width: 15.5rem;
              }
            }
          }
          &.question {
            height: 17rem;
            align-items: flex-start;
            padding: 1rem;
            .box {
              p {
                white-space: pre-wrap;
              }
            }
          }
          &.answer {
            height: 23.6rem;
            align-items: flex-start;
            padding: 1rem;

            .box {
              width: 100%;
              textarea {
                height: 21.2rem;
              }
            }
          }
        }
      }
    }
    .btn-box {
      margin-top: 2.4rem;
      display: flex;
      justify-content: center;
      margin-bottom: 4rem;
      gap: 0.8rem;
      button {
        padding: 1.1rem 1rem;
        white-space: nowrap;
        border: none;
        outline: none;
        border-radius: 0.8rem;
        font-size: 1.5rem;
        width: 10rem;
        &.cancle {
        }
        &.regist {
          background-color: ${(props) => props.theme.btnBlueColor};
          color: ${(props) => props.theme.textWhiteColor};
        }
      }
    }
  }
`;
